import type { ExtractedMessages } from '@Infra/FetchLib/ApiError/extractMessages';
import useBeforeUnloadGuard from '@UI/Hooks/useBeforeUnloadGuard';
import DiscardRenameDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/EditableTitle/ConfirmationDialogs/DiscardRenameDialog';
import RenameFormData from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/EditableTitle/FormData/RenameFormData';
import resolveRenameErrorMessage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/EditableTitle/Helpers/resolveRenameErrorMessage';
import { setEditedTitle } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/EditableTitle/Signals/editedTitleSignal';
import { ClassNames, css } from '@emotion/react';
import { Check, X } from 'lucide-react';
import * as React from 'react';
import { Button, InputText } from '@verclam/ui-kit';

const editorStyle = css`
	display: flex;
	align-items: center;
	gap: 0.25rem;
`;

const fieldStyle = css`
	flex: 1;
	min-width: 0;
`;

const actionsStyle = css`
	display: flex;
	align-items: center;
	flex-shrink: 0;
	margin-bottom: 1.25rem;
`;

const inheritFontStyle = css`
	font: inherit;
`;

type Props = {
	readonly title: string;
	readonly fieldLabel: string;
	/** Resolves on success; rejects with the `ExtractedMessages` of the failure action. */
	readonly onRename: (name: string) => Promise<void>;
};

/**
 * The title as an input with Confirmer / Annuler. Enter confirms and Escape or Annuler cancel. A click
 * outside cancels too, after a confirmation when the name has changed. The input inherits the title
 * font so the title does not jump when it opens.
 */
const TitleEditor: React.FC<Props> = ({ title, fieldLabel, onRename }) => {
	const [formData, setFormData] = React.useState(() => RenameFormData.fromName(title));
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [serverError, setServerError] = React.useState<string | null>(null);
	const [isDiscardOpen, setIsDiscardOpen] = React.useState(false);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const inputRef = React.useRef<HTMLInputElement>(null);

	// Focus and select on mount, and again once a failed submission re-enables the input.
	React.useEffect(() => {
		if (!isSubmitting) {
			inputRef.current?.focus();
			inputRef.current?.select();
		}
	}, [isSubmitting]);

	const hasChanged = formData.name.trim() !== title;
	useBeforeUnloadGuard(hasChanged);

	const handleCancel = React.useCallback(() => {
		setEditedTitle(null);
	}, []);

	const handleChange = React.useCallback((value: string) => {
		setServerError(null);
		setFormData((current) => current.setName(value));
	}, []);

	const handleConfirm = React.useCallback(() => {
		if (isSubmitting) {
			return;
		}

		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		const name = formData.name.trim();

		if (name === title) {
			setEditedTitle(null);
			return;
		}

		setServerError(null);
		setIsSubmitting(true);
		onRename(name).then(
			() => setEditedTitle(null),
			(rejection: ExtractedMessages) => {
				setServerError(resolveRenameErrorMessage(rejection));
				setIsSubmitting(false);
			},
		);
	}, [isSubmitting, formData, title, onRename]);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				handleConfirm();
				return;
			}

			if (event.key === 'Escape') {
				event.preventDefault();
				handleCancel();
			}
		},
		[handleConfirm, handleCancel],
	);

	const handleBlur = React.useCallback(
		(event: React.FocusEvent<HTMLDivElement>) => {
			if (isSubmitting || isDiscardOpen || containerRef.current?.contains(event.relatedTarget)) {
				return;
			}

			// The window lost focus (alt-tab, other tab): the user did not leave the editor.
			if (!document.hasFocus()) {
				return;
			}

			if (hasChanged) {
				setIsDiscardOpen(true);
				return;
			}

			handleCancel();
		},
		[isSubmitting, isDiscardOpen, hasChanged, handleCancel],
	);

	const handleContinue = React.useCallback(() => {
		setIsDiscardOpen(false);
		inputRef.current?.focus();
	}, []);

	// Keeps the focus in the input: a click on a button is not a click outside.
	const handleActionsMouseDown = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
		event.preventDefault();
	}, []);

	return (
		<React.Fragment>
			<ClassNames>
				{({ css: toClassName }) => (
					// biome-ignore lint/a11y/noStaticElementInteractions: focus-out guard only, it adds no interaction of its own.
					<div ref={containerRef} css={editorStyle} onBlur={handleBlur}>
						<div css={fieldStyle}>
							<InputText
								ref={inputRef}
								name="name"
								label={fieldLabel}
								isLabelHidden
								maxLength={180}
								value={formData.name}
								onChange={handleChange}
								onKeyDown={handleKeyDown}
								disabled={isSubmitting}
								inputClassName={toClassName(inheritFontStyle)}
								errorMessage={
									formData.getValidationMessage('name') ?? serverError ?? undefined
								}
								isFormFieldValid={formData.isValid('name') && serverError === null}
							/>
						</div>
						{/* biome-ignore lint/a11y/noStaticElementInteractions: keeps the input focused on a button press; keyboard users reach the buttons by Tab. */}
						<div css={actionsStyle} onMouseDown={handleActionsMouseDown}>
							<Button
								type="button"
								text
								rounded
								size="sm"
								icon={<Check size={16} />}
								aria-label="Confirmer"
								tooltip="Confirmer"
								tooltipOptions={{ position: 'top' }}
								loading={isSubmitting}
								onClick={handleConfirm}
							/>
							<Button
								type="button"
								text
								rounded
								size="sm"
								severity="secondary"
								icon={<X size={16} />}
								aria-label="Annuler"
								tooltip="Annuler"
								tooltipOptions={{ position: 'top' }}
								disabled={isSubmitting}
								onClick={handleCancel}
							/>
						</div>
					</div>
				)}
			</ClassNames>
			<DiscardRenameDialog
				visible={isDiscardOpen}
				onDiscard={handleCancel}
				onContinue={handleContinue}
			/>
		</React.Fragment>
	);
};

export default TitleEditor;
